/**
 * Endpoints de **Finanzas → Control Efectivo** (`/api/finanzas/control-efectivo`).
 *
 * El histórico de fichas trae sus contadores, las bóvedas pendientes en los dos
 * modos, las cuentas bancarias y los permisos de la sesión. Cada ficha tiene sus
 * bóvedas y sus movimientos (depósitos y egresos), y sobre ella se crean y se
 * borran los movimientos o se anula la ficha entera.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type {
  BovedasFichaRespuesta,
  CrearFichaCuerpo,
  DepositosFichaRespuesta,
  FichasEfectivo,
  MovimientoFichaCuerpo,
  PendientesRespuesta,
} from './control-efectivo.types';

export const controlEfectivoKeys = {
  todo: ['finanzas', 'control-efectivo'] as const,
  fichas: (pagina: number, porPagina: number) =>
    ['finanzas', 'control-efectivo', 'fichas', pagina, porPagina] as const,
  pendientes: (modo: string, desde: string, hasta: string) =>
    ['finanzas', 'control-efectivo', 'pendientes', modo, desde, hasta] as const,
  bovedas: (id: number) => ['finanzas', 'control-efectivo', 'bovedas', id] as const,
  depositos: (id: number) => ['finanzas', 'control-efectivo', 'depositos', id] as const,
};

/** Formulario del comprobante (el archivo es opcional). */
function formularioDe(datos: Record<string, string>, archivo?: File | null): FormData {
  const formulario = new FormData();

  Object.entries(datos).forEach(([clave, valor]) => formulario.set(clave, valor));

  if (archivo) formulario.set('archivo', archivo);

  return formulario;
}

/** Cadena de consulta del listado de pendientes. */
export function cadenaDePendientes(modo: string, desde: string, hasta: string): string {
  const parametros = new URLSearchParams({ modo });

  if (desde) parametros.set('desde', desde);
  if (hasta) parametros.set('hasta', hasta);

  return `?${parametros.toString()}`;
}

export const controlEfectivoApi = {
  fichas: (pagina: number, porPagina: number, signal?: AbortSignal): Promise<FichasEfectivo> =>
    apiClient.get<FichasEfectivo>(
      `/api/finanzas/control-efectivo?page=${pagina}&per_page=${porPagina}`,
      { signal },
    ),

  pendientes: (
    modo: 'turno' | 'detallado',
    desde: string,
    hasta: string,
    signal?: AbortSignal,
  ): Promise<PendientesRespuesta> =>
    apiClient.get<PendientesRespuesta>(
      `/api/finanzas/control-efectivo/pendientes${cadenaDePendientes(modo, desde, hasta)}`,
      { signal },
    ),

  bovedas: (id: number, signal?: AbortSignal): Promise<BovedasFichaRespuesta> =>
    apiClient.get<BovedasFichaRespuesta>(`/api/finanzas/control-efectivo/${id}/bovedas`, {
      signal,
    }),

  depositos: (id: number, signal?: AbortSignal): Promise<DepositosFichaRespuesta> =>
    apiClient.get<DepositosFichaRespuesta>(`/api/finanzas/control-efectivo/${id}/depositos`, {
      signal,
    }),

  crearFicha: (datos: CrearFichaCuerpo) =>
    apiClient.post<{ id: number; message: string }>('/api/finanzas/control-efectivo', {
      body: datos,
    }),

  registrarMovimiento: (id: number, datos: MovimientoFichaCuerpo, esEgreso: boolean) =>
    apiClient.post<{ id: number; movement_id: number; message: string }>(
      `/api/finanzas/control-efectivo/${id}/depositos`,
      {
        body: formularioDe(
          {
            fecha: datos.fecha,
            monto: esEgreso ? `-${Math.abs(Number(datos.monto))}` : String(datos.monto),
            referencia: datos.referencia,
            ...(esEgreso ? {} : { cuenta_id: datos.cuenta_id ?? '' }),
          },
          datos.archivo,
        ),
      },
    ),

  borrarMovimiento: (id: number, movimientoId: number) =>
    apiClient.delete<{ message: string }>(
      `/api/finanzas/control-efectivo/${id}/depositos/${movimientoId}`,
    ),

  anularFicha: (id: number) =>
    apiClient.delete<{ message: string; bovedas_liberadas: number }>(
      `/api/finanzas/control-efectivo/${id}`,
    ),
};

/** Histórico de fichas con sus contadores (paginado en el servidor). */
export function useFichasEfectivo(pagina = 1, porPagina = 10) {
  return useQuery({
    queryKey: controlEfectivoKeys.fichas(pagina, porPagina),
    queryFn: ({ signal }) => controlEfectivoApi.fichas(pagina, porPagina, signal),
  });
}

/** Bóvedas pendientes de un modo (y, si es detallado, de un rango de fechas). */
export function usePendientesEfectivo(
  modo: 'turno' | 'detallado',
  desde = '',
  hasta = '',
  habilitado = true,
) {
  return useQuery({
    queryKey: controlEfectivoKeys.pendientes(modo, desde, hasta),
    queryFn: ({ signal }) => controlEfectivoApi.pendientes(modo, desde, hasta, signal),
    enabled: habilitado,
  });
}

/** Bóvedas que componen una ficha. */
export function useBovedasFicha(id: number | null) {
  return useQuery({
    queryKey: controlEfectivoKeys.bovedas(id ?? 0),
    queryFn: ({ signal }) => controlEfectivoApi.bovedas(id as number, signal),
    enabled: Number(id) > 0,
  });
}

/** Depósitos y egresos de una ficha. */
export function useDepositosFicha(id: number | null) {
  return useQuery({
    queryKey: controlEfectivoKeys.depositos(id ?? 0),
    queryFn: ({ signal }) => controlEfectivoApi.depositos(id as number, signal),
    enabled: Number(id) > 0,
  });
}

/** Invalida el histórico y lo que cuelga de una ficha tras cualquier cambio. */
function useRefrescar(id?: number) {
  const cliente = useQueryClient();

  return () => {
    void cliente.invalidateQueries({ queryKey: controlEfectivoKeys.todo });

    if (id) {
      void cliente.invalidateQueries({ queryKey: controlEfectivoKeys.bovedas(id) });
      void cliente.invalidateQueries({ queryKey: controlEfectivoKeys.depositos(id) });
    }
  };
}

export function useCrearFichaEfectivo() {
  const refrescar = useRefrescar();

  return useMutation({
    mutationFn: (datos: CrearFichaCuerpo) => controlEfectivoApi.crearFicha(datos),
    onSuccess: refrescar,
  });
}

export function useRegistrarMovimientoFicha() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      datos,
      esEgreso,
    }: {
      id: number;
      datos: MovimientoFichaCuerpo;
      esEgreso: boolean;
    }) => controlEfectivoApi.registrarMovimiento(id, datos, esEgreso),
    onSuccess: (_datos, variables) => {
      void cliente.invalidateQueries({ queryKey: controlEfectivoKeys.todo });
      void cliente.invalidateQueries({
        queryKey: controlEfectivoKeys.depositos(variables.id),
      });
    },
  });
}

export function useBorrarMovimientoFicha(id: number) {
  const refrescar = useRefrescar(id);

  return useMutation({
    mutationFn: (movimientoId: number) => controlEfectivoApi.borrarMovimiento(id, movimientoId),
    onSuccess: refrescar,
  });
}

export function useAnularFichaEfectivo() {
  const refrescar = useRefrescar();

  return useMutation({
    mutationFn: (id: number) => controlEfectivoApi.anularFicha(id),
    onSuccess: refrescar,
  });
}
